import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Building2, ChevronDown, Users, Check } from 'lucide-react';


const OrganizationHeader = ({ 
  organizationName,
  currentOrganization, 
  onOrganizationChange
}) => {
  const [organizations, setOrganizations] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [switchingTo, setSwitchingTo] = useState(null);

  // Fetch user's organizations
  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await api.get(`/user/organizations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      
      setOrganizations(response.data.organizations || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations([]);
    }
  };

  const handleOrganizationSwitch = async (organizationId) => {
    if (organizationId === currentOrganization?.id) {
      setShowDropdown(false);
      return;
    }

    setSwitchingTo(organizationId);
    setLoading(true);

    try {
      const response = await api.post(
        `/user/switch-organization/${organizationId}`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );

      if (response.data.success) {
        // Update current organization
        onOrganizationChange(response.data.organization);
        
        setShowDropdown(false);
        
        // Reload the page to refresh all data with new team context
        // The backend now uses current_team_id from users table, so no token change needed
        window.location.reload();
      }
    } catch (error) {
      console.error('Error switching organization:', error);
      alert('Failed to switch organization. Please try again.');
    } finally {
      setLoading(false);
      setSwitchingTo(null);
    }
  };

  const hasMultipleOrgs = organizations.length > 1;

  return (
    <div className="relative">
      {/* Organization Header */}
      <div className="flex items-center justify-between px-4 mb-4">
        <button 
          className={`flex items-center flex-1 min-w-0 ${hasMultipleOrgs ? 'hover:bg-slate-800 rounded-lg p-2 -m-2 transition-colors cursor-pointer' : ''}`}
          onClick={() => hasMultipleOrgs && setShowDropdown(!showDropdown)}
          disabled={!hasMultipleOrgs}
        >
          <Building2 className="w-5 h-5 text-purple-400" />
          <h2 className="text-lg font-semibold text-white ml-2 min-w-0 whitespace-normal break-words overflow-wrap-anywhere leading-snug">
            {organizationName}
          </h2>
          
          {/* Subtle chevron - Only show if multiple organizations */}
          {hasMultipleOrgs && (
            <ChevronDown className={`w-4 h-4 text-gray-400 ml-2 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          )}
        </button>
      </div>

      {/* Organizations Dropdown */}
      {showDropdown && hasMultipleOrgs && (
        <>
          {/* Backdrop to close dropdown when clicking outside */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowDropdown(false)}
          />
          
          <div className="absolute top-full left-4 right-4 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
            <div className="py-1">
              <div className="px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-slate-700">
                Switch Organization
              </div>
              {organizations.map((org) => (
                <button
                  key={org.id}
                  onClick={() => handleOrganizationSwitch(org.id)}
                  disabled={loading}
                  className={`w-full flex items-center space-x-3 px-4 py-3 hover:bg-slate-700 text-left transition-colors ${
                    org.id === currentOrganization?.id ? 'bg-slate-700/50' : ''
                  } ${loading && switchingTo === org.id ? 'opacity-50' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    org.is_owner 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                      : 'bg-slate-600'
                  }`}>
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-white truncate">{org.name}</p>
                      {org.id === currentOrganization?.id && (
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-gray-400 capitalize">{org.role}</p>
                  </div>
                  
                  {loading && switchingTo === org.id && (
                    <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrganizationHeader;
